// Shared strength-session resolver.
//
// Every strength surface (Day anchor, PLAN card, session inspector, push
// sheet) renders the coach's applied prescription for the date — its own
// exercise list with EXPLICIT per-set reps/weights, persisted in
// daily_strength_prescription and inlined as `coach_strength_session` on the
// WorkoutRow. This module normalizes that blob into one `ResolvedSession` so
// consumers share the key resolution and the unilateral / label derivations.
//
// Two keys per exercise, and they mean different things:
//   - `key` (`X-<i>`) is the exercise's POSITION in the day's list — the
//     exercises_done flag key. Not an identity.
//   - `actualsKey` is the exercise IDENTITY: `g:<group_id>` for a Speediance
//     catalog exercise, else the free-form name (bodyweight / no-group moves).
//     exercise_weights, actual_set and change_log all key by this, and the
//     coach APPLY + Speediance readback write the same key — so "is this
//     logged?" and "what is the working weight?" lookups line up.

import type { CoachStrengthSession, SpeedianceMappingRow } from './types';

/** A single prescribed set — explicit reps + weight. Timed holds (planks etc.)
 *  carry `durationS` instead of reps. */
export type ResolvedSet = {
  reps: number | null;
  /** Hold duration in seconds for a TIMED set; null for rep-based sets. */
  durationS: number | null;
  weightKg: number;
};

export type ResolvedExercise = {
  /** Position key `X-<i>` — what exercises_done is written under. */
  key: string;
  /** Identity key (`g:<group_id>` | name) — exercise_weights / actual_set key. */
  actualsKey: string;
  name: string;
  /** Human set/rep label derived from the explicit sets, e.g. "4 × 8/side". */
  setsLabel: string;
  isCompound: boolean;
  isUnilateral: boolean;
  /** The prescribed per-set targets, verbatim. Never empty. */
  sets: ResolvedSet[];
  /** Coach's prescribed top-set weight (the heaviest prescribed set). */
  prescribedTopWeightKg: number;
};

export type ResolvedSession = {
  /** Display title — the coach activity's free-form name. */
  title: string;
  exercises: ResolvedExercise[];
};

/**
 * The identity key of a coach exercise. Mirrors the server's APPLY rule
 * (routes/coach-actions.ts) and the readback (`g:${groupId}`), so a key
 * derived here always matches the rows those wrote.
 */
export function exerciseKeyFor(ex: { group_id?: number; name: string }): string {
  return ex.group_id !== undefined ? `g:${ex.group_id}` : ex.name;
}

/** Derive a "N × R" label from the explicit sets. Appends "/side" for
 *  unilateral exercises so the per-side nature reads the same across
 *  surfaces. Timed holds label by seconds ("3 × 50s"). */
function setsLabel(sets: ResolvedSet[], isUnilateral: boolean): string {
  const n = sets.length;
  const side = isUnilateral ? '/side' : '';
  const reps = sets.map((s) => s.reps).filter((r): r is number => r != null);
  if (reps.length > 0) {
    const lo = Math.min(...reps);
    const hi = Math.max(...reps);
    return lo === hi ? `${n} × ${lo}${side}` : `${n} × ${lo}–${hi}${side}`;
  }
  const durs = sets.map((s) => s.durationS).filter((d): d is number => d != null);
  if (durs.length > 0) {
    const lo = Math.min(...durs);
    const hi = Math.max(...durs);
    return lo === hi ? `${n} × ${lo}s${side}` : `${n} × ${lo}–${hi}s${side}`;
  }
  return `${n} set${n === 1 ? '' : 's'}`;
}

/** Name-based unilateral heuristic — the fallback when the exercise has no
 *  mapping row (free-form name; "per leg / side / arm" cues it). */
function unilateralFromName(name: string): boolean {
  return /\b(per leg|per side|per arm|\/leg|\/side|\/arm)\b/i.test(name);
}

/**
 * Resolve the strength session to render for a date from the coach's applied
 * prescription. Returns null when there is no (or an empty) prescription —
 * the caller renders the "no prescription" tile.
 *
 * `coachTitle` is the coach activity's free-form name (e.g. "Upper Push —
 * peak volume"); a generic label is used when absent.
 *
 * `mappingRows` is the authoritative source for whether each exercise is
 * unilateral (group_id → is_unilateral) — the coach's free-form name often
 * omits the "/side" cue (e.g. "Pallof Press"). Exercises without a mapping row
 * fall back to the name heuristic.
 */
export function resolveStrengthSession(args: {
  coachSession: CoachStrengthSession | null | undefined;
  coachTitle?: string | null;
  mappingRows?: SpeedianceMappingRow[];
}): ResolvedSession | null {
  const session = args.coachSession;
  if (!session || session.exercises.length === 0) return null;
  const unilateralByGroup = new Map<number, boolean>();
  for (const m of args.mappingRows ?? []) unilateralByGroup.set(m.group_id, m.is_unilateral);
  const exercises: ResolvedExercise[] = [];
  session.exercises.forEach((ex, i) => {
    // The backend requires >= 1 set per exercise; a degenerate blob with none
    // has nothing to render, so drop the exercise rather than fake a set.
    if (ex.sets.length === 0) return;
    const mapped = ex.group_id !== undefined ? unilateralByGroup.get(ex.group_id) : undefined;
    const isUnilateral = mapped ?? unilateralFromName(ex.name);
    const sets = ex.sets.map((s) => ({
      reps: s.reps ?? null,
      durationS: s.duration_s ?? null,
      weightKg: s.weight_kg,
    }));
    exercises.push({
      key: `X-${i}`,
      actualsKey: exerciseKeyFor(ex),
      name: ex.name,
      setsLabel: setsLabel(sets, isUnilateral),
      isCompound: i === 0,
      isUnilateral,
      sets,
      prescribedTopWeightKg: Math.max(...sets.map((s) => s.weightKg)),
    });
  });
  if (exercises.length === 0) return null;
  return {
    title: args.coachTitle?.trim() || 'Coach strength session',
    exercises,
  };
}
